import { DEG, TAU, clamp01 } from '../../core/math';
import { resolvePalette, type ResolvedPalette } from '../../core/palette';
import { createCanvas, get2d } from '../../core/text/rasterize';
import type { Ctx2D, RenderTarget, TechniqueContext } from '../types';
import type { BoidsValues } from './params';

/**
 * Reynolds boids (separation, alignment, cohesion) steered by the text field.
 *
 * The neighbour search is the quadratic part, so agents are bucketed into a uniform
 * grid sized to the *largest* of the three radii and only nearby cells are visited. The
 * grid is rebuilt each step rather than updated incrementally — at a few thousand
 * agents the rebuild is two linear passes and simpler than maintaining membership.
 *
 * Trails are accumulated into an offscreen buffer instead of being kept as per-agent
 * history: a 20 000-agent swarm with a 200-step trail would be four million points to
 * store and redraw every frame, against one `drawImage`.
 */
export interface BoidsState {
  x: Float32Array;
  y: Float32Array;
  vx: Float32Array;
  vy: Float32Array;
  tint: Float32Array;
  count: number;

  /** CSR-style bucket index, rebuilt each step into preallocated scratch. */
  cellStart: Int32Array;
  cellItems: Int32Array;
  cellCounts: Int32Array;
  cellCursor: Int32Array;
  cols: number;
  rows: number;
  cell: number;

  buffer: OffscreenCanvas | HTMLCanvasElement;
  bctx: Ctx2D;
  palette: ResolvedPalette;
  /** Pre-built colour strings and per-colour segment buffers for the trail. */
  trailStyles: string[];
  trailBuckets: { coords: Float32Array; n: number }[];
  stepsDone: number;
  targetSteps: number;
}

/**
 * Trail segments are bucketed by quantised colour and stroked one path per bucket.
 *
 * Setting `strokeStyle` re-parses a CSS colour string and every `stroke()` revalidates
 * the context; doing that once per agent per step is hundreds of thousands of calls and
 * was measured at ten seconds for a default swarm. Thirty-two colour steps are well
 * below what a semi-transparent hairline can show.
 */
const TRAIL_BUCKETS = 32;

/**
 * Upper bound on neighbours examined per agent per step.
 *
 * The grid keeps the search local, but locality is not the problem: text attraction
 * *clusters* the swarm, and a cluster puts hundreds of agents into one cell, so the
 * per-agent cost grows as the simulation converges — measured at 9.3 ms per step early
 * on and several times that once the swarm had gathered on the letterforms.
 *
 * Capping the count bounds the worst case. It is also defensible on its own terms:
 * real flocking models assume a fixed number of influencing neighbours (Ballerini et
 * al. found starlings track roughly seven), not everything within a radius.
 */
const MAX_NEIGHBOURS = 40;

function values(ctx: TechniqueContext): BoidsValues {
  return ctx.values as unknown as BoidsValues;
}

export function init(ctx: TechniqueContext): BoidsState {
  const v = values(ctx);
  const count = Math.max(1, v.count);
  const cell = Math.max(4, Math.max(v.separationRadius, v.alignmentRadius, v.cohesionRadius));
  const cols = Math.ceil(ctx.width / cell) + 1;
  const rows = Math.ceil(ctx.height / cell) + 1;

  const state: BoidsState = {
    x: new Float32Array(count),
    y: new Float32Array(count),
    vx: new Float32Array(count),
    vy: new Float32Array(count),
    tint: new Float32Array(count),
    count,
    cellStart: new Int32Array(cols * rows + 1),
    cellItems: new Int32Array(count),
    cellCounts: new Int32Array(cols * rows),
    cellCursor: new Int32Array(cols * rows),
    cols,
    rows,
    cell,
    buffer: createCanvas(ctx.width, ctx.height),
    bctx: get2d(createCanvas(1, 1)),
    palette: resolvePalette(v.palette, ctx.rng.fork(1)),
    trailStyles: [],
    trailBuckets: Array.from({ length: TRAIL_BUCKETS }, () => ({ coords: new Float32Array(512), n: 0 })),
    stepsDone: 0,
    targetSteps: v.steps,
  };
  state.bctx = get2d(state.buffer);
  state.bctx.lineCap = 'round';

  const palette = state.palette;
  state.trailStyles = Array.from({ length: TRAIL_BUCKETS }, (_, i) =>
    v.colorMode === 'solid' ? palette.stop(0, v.opacity) : palette.at(i / (TRAIL_BUCKETS - 1), v.opacity),
  );

  // Seed on the outline when the swarm is meant to form the letters, otherwise spread.
  const samples = v.textMode === 'shape' ? ctx.field.sampleOutline(Math.min(4000, count)) : [];
  for (let i = 0; i < count; i++) {
    if (samples.length) {
      const s = samples[ctx.rng.int(0, samples.length - 1)];
      const spread = 30 + ctx.rng.next() * 90;
      state.x[i] = s.x + s.nx * spread + ctx.rng.signed(20);
      state.y[i] = s.y + s.ny * spread + ctx.rng.signed(20);
    } else {
      state.x[i] = ctx.rng.next() * ctx.width;
      state.y[i] = ctx.rng.next() * ctx.height;
    }
    const angle = ctx.rng.next() * TAU;
    state.vx[i] = Math.cos(angle) * v.maxSpeed;
    state.vy[i] = Math.sin(angle) * v.maxSpeed;
    state.tint[i] = ctx.rng.next();
  }

  return state;
}

/**
 * Rebuilds the bucket index in place.
 *
 * The scratch arrays are preallocated on the state: this runs once per simulation step,
 * hundreds of times per render, and allocating two typed arrays each time was a
 * measurable share of the frame.
 */
function rebuildGrid(state: BoidsState): void {
  const cells = state.cols * state.rows;
  const counts = state.cellCounts;
  const cursor = state.cellCursor;
  counts.fill(0);
  cursor.fill(0);

  const index = (i: number): number => {
    const cx = Math.min(state.cols - 1, Math.max(0, (state.x[i] / state.cell) | 0));
    const cy = Math.min(state.rows - 1, Math.max(0, (state.y[i] / state.cell) | 0));
    return cy * state.cols + cx;
  };

  for (let i = 0; i < state.count; i++) counts[index(i)]++;
  let running = 0;
  for (let c = 0; c < cells; c++) {
    state.cellStart[c] = running;
    running += counts[c];
  }
  state.cellStart[cells] = running;

  for (let i = 0; i < state.count; i++) {
    const c = index(i);
    state.cellItems[state.cellStart[c] + cursor[c]++] = i;
  }
}

export function step(state: BoidsState, _dt: number, ctx: TechniqueContext): BoidsState {
  if (state.stepsDone >= state.targetSteps) return state;
  const v = values(ctx);

  // Several simulation steps per frame; the swarm needs hundreds to settle.
  const perFrame = Math.max(1, Math.round(4 * clamp01(ctx.quality)));
  const cosView = Math.cos((v.viewAngle * DEG) / 2);
  const sepR2 = v.separationRadius ** 2;
  const alignR2 = v.alignmentRadius ** 2;
  const cohR2 = v.cohesionRadius ** 2;
  const searchRadius = Math.max(v.separationRadius, v.alignmentRadius, v.cohesionRadius);

  for (let frame = 0; frame < perFrame && state.stepsDone < state.targetSteps; frame++) {
    rebuildGrid(state);
    const bctx = state.bctx;
    bctx.lineWidth = v.size;

    // Trail length is a fade rate rather than stored history: erasing a fraction of the
    // buffer each step leaves exactly the last `trailLength` steps visible.
    if (v.display === 'trail' && v.trailLength < state.targetSteps) {
      bctx.globalCompositeOperation = 'destination-out';
      bctx.fillStyle = `rgba(0,0,0,${(1 / v.trailLength).toFixed(4)})`;
      bctx.fillRect(0, 0, ctx.width, ctx.height);
      bctx.globalCompositeOperation = 'source-over';
    }

    // Hoisted into locals for the inner loop: reading `state.x[j]` through the object
    // on every neighbour test is a property lookup the engine cannot hoist itself, and
    // at eighty million tests per run that dominates everything else.
    const sx = state.x;
    const sy = state.y;
    const svx = state.vx;
    const svy = state.vy;
    const cellStart = state.cellStart;
    const cellItems = state.cellItems;
    const cols = state.cols;
    const rows = state.rows;
    const cellSize = state.cell;
    const maxR2 = Math.max(sepR2, alignR2, cohR2);

    for (let i = 0; i < state.count; i++) {
      const px = sx[i];
      const py = sy[i];
      const pvx = svx[i];
      const pvy = svy[i];
      const speed = Math.sqrt(pvx * pvx + pvy * pvy) || 1;

      let sepX = 0;
      let sepY = 0;
      let alignX = 0;
      let alignY = 0;
      let cohX = 0;
      let cohY = 0;
      let sepN = 0;
      let alignN = 0;
      let cohN = 0;

      const minCx = Math.max(0, ((px - searchRadius) / cellSize) | 0);
      const maxCx = Math.min(cols - 1, ((px + searchRadius) / cellSize) | 0);
      const minCy = Math.max(0, ((py - searchRadius) / cellSize) | 0);
      const maxCy = Math.min(rows - 1, ((py + searchRadius) / cellSize) | 0);

      let examined = 0;
      scan: for (let cy = minCy; cy <= maxCy; cy++) {
        const rowBase = cy * cols;
        for (let cx = minCx; cx <= maxCx; cx++) {
          const cellIndex = rowBase + cx;
          const end = cellStart[cellIndex + 1];
          for (let k = cellStart[cellIndex]; k < end; k++) {
            if (++examined > MAX_NEIGHBOURS) break scan;
            const j = cellItems[k];
            if (j === i) continue;
            const dx = sx[j] - px;
            const dy = sy[j] - py;
            const d2 = dx * dx + dy * dy;
            // Cheap rejection first: most candidates in the scanned cells are out of
            // range for every rule, and this skips them before any square root.
            if (d2 >= maxR2 || d2 < 1e-6) continue;

            // Field of view: a boid does not react to what is behind it.
            if ((dx * pvx + dy * pvy) < cosView * Math.sqrt(d2) * speed) continue;

            if (d2 < sepR2) {
              sepX -= dx / d2;
              sepY -= dy / d2;
              sepN++;
            }
            if (d2 < alignR2) {
              alignX += svx[j];
              alignY += svy[j];
              alignN++;
            }
            if (d2 < cohR2) {
              cohX += sx[j];
              cohY += sy[j];
              cohN++;
            }
          }
        }
      }

      // Reynolds steering, written out rather than in a closure: a closure here is one
      // allocation per rule per agent per step — on this workload that was the single
      // largest cost in the whole technique.
      let ax = 0;
      let ay = 0;
      let dx = 0;
      let dy = 0;
      let weight = 0;
      let rule = 0;

      for (;;) {
        if (rule === 0) {
          if (sepN === 0) {
            rule++;
            continue;
          }
          dx = sepX;
          dy = sepY;
          weight = v.separationWeight;
        } else if (rule === 1) {
          if (alignN === 0) {
            rule++;
            continue;
          }
          dx = alignX / alignN;
          dy = alignY / alignN;
          weight = v.alignmentWeight;
        } else if (rule === 2) {
          if (cohN === 0) {
            rule++;
            continue;
          }
          dx = cohX / cohN - px;
          dy = cohY / cohN - py;
          weight = v.cohesionWeight;
        } else if (rule === 3) {
          if (v.textMode === 'none') break;
          const signed = ctx.field.distance(px, py);
          const g = ctx.field.gradient(px, py);
          if (g[0] === 0 && g[1] === 0) break;
          if (v.textMode === 'avoid') {
            dx = g[0];
            dy = g[1];
            weight = v.textWeight;
          } else if (v.textMode === 'orbit') {
            dx = -g[1];
            dy = g[0];
            weight = v.textWeight;
          } else {
            // Shape: pull towards the contour from whichever side the agent is on.
            const sign = signed > 0 ? -1 : 1;
            dx = g[0] * sign;
            dy = g[1] * sign;
            weight = v.textWeight * clamp01(Math.abs(signed) / 60);
          }
        } else break;

        const len = Math.sqrt(dx * dx + dy * dy);
        if (len > 1e-6) {
          let sx = (dx / len) * v.maxSpeed - pvx;
          let sy = (dy / len) * v.maxSpeed - pvy;
          const slen = Math.sqrt(sx * sx + sy * sy);
          if (slen > v.maxForce) {
            sx = (sx / slen) * v.maxForce;
            sy = (sy / slen) * v.maxForce;
          }
          ax += sx * weight;
          ay += sy * weight;
        }
        rule++;
      }

      let nvx = pvx + ax;
      let nvy = pvy + ay;
      const nspeed = Math.sqrt(nvx * nvx + nvy * nvy);
      if (nspeed > v.maxSpeed) {
        nvx = (nvx / nspeed) * v.maxSpeed;
        nvy = (nvy / nspeed) * v.maxSpeed;
      }

      let nx = px + nvx;
      let ny = py + nvy;
      // Wrap rather than bounce; a bounced swarm piles up along the edges.
      if (nx < 0) nx += ctx.width;
      else if (nx >= ctx.width) nx -= ctx.width;
      if (ny < 0) ny += ctx.height;
      else if (ny >= ctx.height) ny -= ctx.height;

      const jumped = Math.abs(nx - px) > v.maxSpeed * 3 || Math.abs(ny - py) > v.maxSpeed * 3;

      if (v.display === 'trail' && !jumped) {
        const bucket = state.trailBuckets[colorBucket(state, v, i, nspeed, Math.atan2(nvy, nvx), ctx)];
        if (bucket.n + 4 > bucket.coords.length) {
          const grown = new Float32Array(bucket.coords.length * 2);
          grown.set(bucket.coords);
          bucket.coords = grown;
        }
        bucket.coords[bucket.n++] = px;
        bucket.coords[bucket.n++] = py;
        bucket.coords[bucket.n++] = nx;
        bucket.coords[bucket.n++] = ny;
      }

      sx[i] = nx;
      sy[i] = ny;
      svx[i] = nvx;
      svy[i] = nvy;
    }

    if (v.display === 'trail') flushTrail(state);
    state.stepsDone++;
  }

  return state;
}

/** Strokes each non-empty trail bucket as a single path and empties it. */
function flushTrail(state: BoidsState): void {
  const bctx = state.bctx;
  for (let b = 0; b < state.trailBuckets.length; b++) {
    const bucket = state.trailBuckets[b];
    if (bucket.n === 0) continue;
    bctx.strokeStyle = state.trailStyles[b];
    bctx.beginPath();
    const c = bucket.coords;
    for (let i = 0; i < bucket.n; i += 4) {
      bctx.moveTo(c[i], c[i + 1]);
      bctx.lineTo(c[i + 2], c[i + 3]);
    }
    bctx.stroke();
    bucket.n = 0;
  }
}

/** Quantised colour index, matching `trailStyles`. */
function colorBucket(
  state: BoidsState,
  v: BoidsValues,
  i: number,
  speed: number,
  heading: number,
  ctx: TechniqueContext,
): number {
  let t: number;
  switch (v.colorMode) {
    case 'speed':
      t = clamp01(speed / Math.max(0.01, v.maxSpeed));
      break;
    case 'heading':
      t = ((((heading % TAU) + TAU) / TAU) % 1);
      break;
    case 'distance':
      t = clamp01(Math.abs(ctx.field.distance(state.x[i], state.y[i])) / Math.max(1, ctx.field.maxOutside));
      break;
    case 'random':
      t = state.tint[i];
      break;
    default:
      t = 0;
  }
  return Math.min(TRAIL_BUCKETS - 1, (t * TRAIL_BUCKETS) | 0);
}

function colorFor(
  state: BoidsState,
  v: BoidsValues,
  i: number,
  speed: number,
  heading: number,
  ctx: TechniqueContext,
): string {
  let t: number;
  switch (v.colorMode) {
    case 'speed':
      t = clamp01(speed / Math.max(0.01, v.maxSpeed));
      break;
    case 'heading':
      t = ((heading % TAU) + TAU) / TAU % 1;
      break;
    case 'distance':
      t = clamp01(Math.abs(ctx.field.distance(state.x[i], state.y[i])) / Math.max(1, ctx.field.maxOutside));
      break;
    case 'random':
      t = state.tint[i];
      break;
    default:
      t = 0;
  }
  return v.colorMode === 'solid' ? state.palette.stop(0, v.opacity) : state.palette.at(t, v.opacity);
}

export function isComplete(state: BoidsState): boolean {
  return state.stepsDone >= state.targetSteps;
}

export function draw(state: BoidsState, target: RenderTarget, ctx: TechniqueContext): void {
  const v = values(ctx);
  const c = target.ctx;

  if (v.display === 'trail') {
    c.drawImage(state.buffer as CanvasImageSource, 0, 0, target.width, target.height);
    return;
  }

  for (let i = 0; i < state.count; i++) {
    const speed = Math.hypot(state.vx[i], state.vy[i]);
    const heading = Math.atan2(state.vy[i], state.vx[i]);
    const color = colorFor(state, v, i, speed, heading, ctx);

    if (v.display === 'dot') {
      c.fillStyle = color;
      c.beginPath();
      c.arc(state.x[i], state.y[i], v.size, 0, TAU);
      c.fill();
    } else if (v.display === 'line') {
      c.strokeStyle = color;
      c.lineWidth = Math.max(0.4, v.size * 0.5);
      c.beginPath();
      c.moveTo(state.x[i], state.y[i]);
      c.lineTo(state.x[i] - Math.cos(heading) * v.size * 4, state.y[i] - Math.sin(heading) * v.size * 4);
      c.stroke();
    } else {
      const s = v.size * 2;
      c.fillStyle = color;
      c.beginPath();
      c.moveTo(state.x[i] + Math.cos(heading) * s, state.y[i] + Math.sin(heading) * s);
      c.lineTo(state.x[i] + Math.cos(heading + 2.4) * s * 0.6, state.y[i] + Math.sin(heading + 2.4) * s * 0.6);
      c.lineTo(state.x[i] + Math.cos(heading - 2.4) * s * 0.6, state.y[i] + Math.sin(heading - 2.4) * s * 0.6);
      c.closePath();
      c.fill();
    }
  }
}

export function elementCount(state: BoidsState): number {
  return state.count;
}
